import React, { useEffect, useState } from 'react';
import { api, EstimateBuilderDocDto } from '../../services/api';
import { Card, Btn, Badge, Input, C } from '../../components/UI';
import { EstimateRequestForm } from '../../components/EstimateRequestForm';
import { SignaturePad } from '../../components/SignaturePad';
import { PaymentForm } from '../../components/PaymentForm';

// ── "My Estimates" — a logged-in customer's own requests, from submission through
// resolution, including approving/declining directly here (mirrors the public token page's
// flow — same SignaturePad, same optional-signature approve — so a customer doesn't have to dig
// up the emailed link just to respond).

export function CustomerEstimatesView({ userName, userEmail, initialSelectedId }: { userName?: string; userEmail?: string; initialSelectedId?: string | null }) {
    const [estimates, setEstimates] = useState<EstimateBuilderDocDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
    const [showForm, setShowForm] = useState(false);

    const load = () => {
        setLoading(true); setError('');
        api.getMyEstimatesAsCustomer().then(setEstimates).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const selected = estimates.find(e => e.id === selectedId) ?? null;
    if (selected) return <CustomerEstimateDetail estimate={selected} onBack={() => { setSelectedId(null); load(); }} />;

    if (!loading && error) {
        return (
            <Card style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
                <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>
                <Btn onClick={load}>Try again</Btn>
            </Card>
        );
    }

    if (showForm || (!loading && estimates.length === 0)) {
        return (
            <Card style={{ maxWidth: 480, margin: '0 auto' }}>
                {estimates.length > 0 && (
                    <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                        ← Back to my estimates
                    </button>
                )}
                <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>📋 Request an Estimate</h2>
                <p style={{ margin: '0 0 20px', fontSize: 13, color: C.textSub }}>Tell us about the job and attach a few photos — we'll follow up with a real quote.</p>
                <EstimateRequestForm
                    lockedName={userName}
                    lockedEmail={userEmail}
                    onSubmit={async body => { await api.requestEstimate(body); load(); setShowForm(false); }}
                />
            </Card>
        );
    }

    return (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>📋 My Estimates</h2>
                <Btn size="sm" onClick={() => setShowForm(true)}>+ New request</Btn>
            </div>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {loading ? (
                <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {estimates.map(e => (
                        <Card key={e.id} style={{ cursor: 'pointer' }}>
                            <div onClick={() => setSelectedId(e.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <strong>EB-{e.number}</strong>
                                        <Badge label={statusLabel(e.status)} />
                                    </div>
                                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>{e.jobSiteAddress}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 700, fontSize: 15 }}>${e.totalAmount.toFixed(2)}</div>
                                    <div style={{ fontSize: 11, color: C.textMuted }}>{new Date(e.createdAt).toLocaleDateString()}</div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

function statusLabel(status: string): string {
    if (status === 'Requested') return 'Submitted — awaiting review';
    if (status === 'Draft') return 'Being prepared';
    if (status === 'Sent' || status === 'Viewed') return 'Awaiting your approval';
    return status;
}

function getGeo(): Promise<{ lat?: number; lng?: number }> {
    return new Promise(resolve => {
        if (!navigator.geolocation) { resolve({}); return; }
        navigator.geolocation.getCurrentPosition(
            pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve({}),
            { timeout: 4000 }
        );
    });
}

function CustomerEstimateDetail({ estimate: initial, onBack }: { estimate: EstimateBuilderDocDto; onBack: () => void }) {
    const [estimate, setEstimate] = useState(initial);
    const [mode, setMode] = useState<'view' | 'signing' | 'declining' | 'paying'>('view');
    const [signature, setSignature] = useState<string | null>(null);
    const [signedByName, setSignedByName] = useState('');
    const [declineNote, setDeclineNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [payClientSecret, setPayClientSecret] = useState<string | null>(null);
    const [payAmount, setPayAmount] = useState(0);
    // true when paying for an estimate already approved in person (Runner-witnessed signing) —
    // just needs a card, not another approveMyEstimate() call.
    const [payingStandalone, setPayingStandalone] = useState(false);

    const canRespond = estimate.status === 'Sent' || estimate.status === 'Viewed';

    const approve = async () => {
        setBusy(true); setError('');
        try {
            const geo = await getGeo();
            const updated = await api.approveMyEstimate(estimate.id, {
                signatureBase64Png: signature,
                lat: geo.lat, lng: geo.lng,
                signedByName: signedByName.trim() || undefined,
            });
            setEstimate(updated);
            setMode('view');
        } catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    };

    const decline = async () => {
        setBusy(true); setError('');
        try {
            const updated = await api.declineMyEstimate(estimate.id, declineNote.trim() || undefined);
            setEstimate(updated);
            setMode('view');
        } catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    };

    // Card authorization (not a charge) ahead of approval — same manual-capture pattern used to
    // book a normal job, just against the estimate's total instead of a job.
    const startPayment = async (standalone: boolean) => {
        setBusy(true); setError('');
        try {
            const { clientSecret, amount } = await api.createMyEstimatePaymentIntent(estimate.id);
            setPayClientSecret(clientSecret);
            setPayAmount(amount);
            setPayingStandalone(standalone);
            setMode('paying');
        } catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    };

    const onPaymentSuccess = async () => {
        setBusy(true); setError('');
        try {
            const updated = await api.confirmMyEstimatePayment(estimate.id);
            if (payingStandalone) { setEstimate(updated); setMode('view'); }
            else { await approve(); }
        } catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    };

    return (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
                ← Back to my estimates
            </button>
            <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h2 style={{ margin: 0, fontSize: 20 }}>EB-{estimate.number}</h2>
                    <Badge label={statusLabel(estimate.status)} />
                </div>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 4, marginBottom: 16 }}>Job site: {estimate.jobSiteAddress}</div>

                {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

                {estimate.status === 'Approved' && (
                    <div style={{ background: C.greenBg, color: C.green, borderRadius: 8, padding: 12, fontSize: 13, marginBottom: 16 }}>
                        ✓ You approved this estimate{estimate.respondedAt ? ` on ${new Date(estimate.respondedAt).toLocaleDateString()}` : ''}.
                        {estimate.convertedJobId && ' It has been converted to a job.'}
                        {!estimate.paymentAuthorized && ' One more step below — add a payment method to get scheduled.'}
                    </div>
                )}
                {estimate.status === 'Declined' && (
                    <div style={{ background: '#FDECEA', color: '#9B1C1C', borderRadius: 8, padding: 12, fontSize: 13, marginBottom: 16 }}>
                        You declined this estimate{estimate.respondedAt ? ` on ${new Date(estimate.respondedAt).toLocaleDateString()}` : ''}.
                    </div>
                )}
                {(estimate.status === 'Requested' || estimate.status === 'Draft') && (
                    <div style={{ background: '#EEF2FF', color: '#3730A3', borderRadius: 8, padding: 12, fontSize: 13, marginBottom: 16 }}>
                        We've received your request and are putting together your estimate — we'll follow up soon.
                    </div>
                )}

                {estimate.requestNotes && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>Your notes</div>
                        <p style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}>{estimate.requestNotes}</p>
                    </div>
                )}
                {estimate.requestPhotoUrls.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>Photos</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                            {estimate.requestPhotoUrls.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                    <img src={url} alt={`Photo ${i + 1}`} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: `1px solid ${C.border}` }} />
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {estimate.lineItems.filter(li => li.isSelected).length > 0 && (
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>Line items</div>
                        {estimate.lineItems.filter(li => li.isSelected).map(li => (
                            <div key={li.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.borderLight}` }}>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{li.description}</div>
                                    <div style={{ fontSize: 12, color: C.textSub }}>{li.quantity} {li.unit} × ${li.unitPrice.toFixed(2)}</div>
                                </div>
                                <div style={{ fontWeight: 700 }}>${li.lineTotal.toFixed(2)}</div>
                            </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 16, fontWeight: 800 }}>
                            <span>Total</span>
                            <span>${estimate.totalAmount.toFixed(2)}</span>
                        </div>
                    </div>
                )}

                {canRespond && mode === 'view' && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                        <Btn variant="outline" onClick={() => setMode('declining')} disabled={busy} fullWidth>Decline</Btn>
                        <Btn onClick={() => setMode('signing')} disabled={busy} fullWidth>Approve estimate</Btn>
                    </div>
                )}

                {estimate.status === 'Approved' && !estimate.paymentAuthorized && mode === 'view' && (
                    <Btn onClick={() => startPayment(true)} disabled={busy} fullWidth style={{ marginTop: 20 }}>
                        {busy ? 'Loading…' : 'Add payment method'}
                    </Btn>
                )}

                {canRespond && mode === 'signing' && (
                    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 20, paddingTop: 16 }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Approve &amp; sign</h3>
                        <Input label="Your name (optional)" value={signedByName} onChange={setSignedByName} placeholder="Full name" />
                        <div style={{ marginTop: 10, marginBottom: 14 }}>
                            <SignaturePad onChange={setSignature} />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={() => setMode('view')} disabled={busy} fullWidth>Back</Btn>
                            <Btn onClick={() => startPayment(false)} disabled={busy} fullWidth>{busy ? 'Loading…' : 'Continue to payment'}</Btn>
                        </div>
                    </div>
                )}

                {mode === 'paying' && payClientSecret && (
                    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 20, paddingTop: 16 }}>
                        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Add a payment method</h3>
                        <p style={{ fontSize: 13, color: C.textSub, margin: '0 0 14px' }}>Your card is authorized now, not charged — you're only billed once the job is complete.</p>
                        <PaymentForm
                            jobId={estimate.id}
                            amount={payAmount}
                            clientSecret={payClientSecret}
                            onSuccess={onPaymentSuccess}
                            onCancel={() => setMode(payingStandalone ? 'view' : 'signing')}
                        />
                    </div>
                )}

                {canRespond && mode === 'declining' && (
                    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 20, paddingTop: 16 }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Decline this estimate</h3>
                        <Input label="Note (optional)" value={declineNote} onChange={setDeclineNote} placeholder="Let us know why, if you'd like" />
                        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                            <Btn variant="outline" onClick={() => setMode('view')} disabled={busy} fullWidth>Back</Btn>
                            <Btn onClick={decline} disabled={busy} fullWidth>{busy ? 'Submitting…' : 'Confirm decline'}</Btn>
                        </div>
                    </div>
                )}

                {estimate.contractDisclaimerText && (
                    <p style={{ fontSize: 11, color: C.textMuted, marginTop: 20 }}>{estimate.contractDisclaimerText}</p>
                )}
            </Card>
        </div>
    );
}
